import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { Box, Button, Grid, Stack, Typography } from '@mui/material';
import {
  DataGridPro,
  gridClasses,
  GridColDef,
  GridEventListener,
  GridRowSelectionModel,
  GridSortModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { useSnackbar } from 'notistack';
import { useCallback, useState } from 'react';

import { TrpcLoadingInfo } from '../../../components/general';
import { useDurchgaengeStatusMutation } from '../../../components/general/hooks/useDurchgaengeStatusMutation.ts';
import {
  ablaufEndColumn,
  ablaufStartColumn,
  akaDurchgangIdColumn,
  akaVeranstaltungIdColumn,
  durchgangExpertsColumn,
  durchgangMultipleRückmeldungColumn,
  durchgangSapIdColumn,
  durchgangsartColumn,
  durchgangWoColumn,
  endColumn,
  expertsColumn,
  leistungIdColumn,
  leistungIndexColumn,
  modulnummerColumn,
  modulterminSingleRückmeldungColumn,
  startColumn,
  statusAPColumn,
  statusColumn,
  terminartColumn,
  veranstaltungSapIdColumn,
  veranstaltungWoColumn,
} from '../../../components/general/TableColumns';
import {
  AkaDurchgangId,
  ausschreibungStatus,
  GetDurchgaengeByAkaProduktIdsOutput,
  Leistung,
  ThemaFromThemaRouter,
  VivaStatus,
} from '../../../dtos';
import { elevateColorPalette } from '../../../theme/colorPalette';
import { trpc } from '../../../trpc';
import { localStorageApi, localStorageKeys } from '../../../utils';
import { getDetailPanelHeight } from '../../../utils/getDetailPanelHeight.ts';
import {
  getLeistungstemplatesFromModul,
  type LeistungstemplateFromModul,
} from '../../../utils/getLeistungstemplatesFromModul.ts';
import { sortVivaStatus } from '../../../utils/sortVivaStatus.ts';
import {
  getAllLeistungen,
  getLowestAusschreibungStatus,
  getNumberOfUniqueAusschreibungStatuses,
} from '../../../utils/teilleistung.ts';
import { unique } from '../../../utils/unique.ts';
import { VeranstaltungFehlerCell } from '../VeranstaltungListing/VeranstaltungFehlerCell';
import { DurchgangFehlerCell } from './DurchgangFehlerCell';
import { content } from './DurchgangListing.content';

type DurchgangElement = GetDurchgaengeByAkaProduktIdsOutput[number];
type ModulterminWithLeistungElement = DurchgangElement['modultermine'][number] & {
  leistung?: Leistung;
  leistungstemplate?: LeistungstemplateFromModul;
};

const durchgangsColumns = (): GridColDef<DurchgangElement>[] => [
  akaDurchgangIdColumn(),
  durchgangSapIdColumn(),
  startColumn(),
  endColumn(),
  statusColumn((row) => row.modultermine.map((mt) => mt.vivaStatus).filter(unique).length),
  statusAPColumn(
    (row) => getLowestAusschreibungStatus(row.modultermine.flatMap((m) => m.teilleistungen)),
    (row) => getNumberOfUniqueAusschreibungStatuses(row.modultermine.flatMap((m) => m.teilleistungen)),
  ),
  durchgangMultipleRückmeldungColumn(),
  durchgangWoColumn(),
  durchgangExpertsColumn(),
  durchgangsartColumn(),
  {
    field: 'fehler',
    headerName: content.columnLabels.fehler,
    flex: 0.5,
    renderCell: (params) => <DurchgangFehlerCell {...params} />,
    valueGetter: () => 'fehler',
  },
];

const modulterminColumns = (): GridColDef<ModulterminWithLeistungElement>[] => [
  modulnummerColumn(),
  akaVeranstaltungIdColumn(),
  leistungIdColumn((row) => row.leistung),
  leistungIndexColumn((row) => row.leistungstemplate),
  veranstaltungSapIdColumn(),
  ablaufStartColumn(),
  ablaufEndColumn(),
  statusColumn(),
  statusAPColumn((row) => row.leistung?.ausschreibung?.status ?? ausschreibungStatus.KEINE_AUSSCHREIBUNG),
  modulterminSingleRückmeldungColumn(),
  veranstaltungWoColumn(),
  expertsColumn(),
  terminartColumn(),
  {
    field: 'fehler',
    headerName: content.columnLabels.fehler,
    renderCell: (params) => <VeranstaltungFehlerCell {...params} />,
    flex: 0.5,
    valueGetter: () => 'fehler',
  },
];

type DurchgangListingProps = {
  readonly thema: ThemaFromThemaRouter;
};

const DetailPanelExpandIcon = () => <ExpandMore sx={{ p: 0 }} />;
const DetailPanelCollapseIcon = () => <ExpandLess sx={{ p: 0 }} />;

function getDetailPanelContent(
  thema: ThemaFromThemaRouter,
  { row }: { row: GetDurchgaengeByAkaProduktIdsOutput[number] },
) {
  const rows = row.modultermine.flatMap<ModulterminWithLeistungElement>((modultermin) => {
    const modul = thema.module.find((m) => m.modulnummer === modultermin.modul?.modulnummer);
    const leistungstemplates = modul ? getLeistungstemplatesFromModul(modul) : [];
    const leistungen = getAllLeistungen(modultermin.teilleistungen);

    if (leistungstemplates.length === 0) {
      return [{ ...modultermin, leistung: undefined, leistungstemplate: undefined }];
    }

    return leistungstemplates.map((leistungstemplate) => ({
      ...modultermin,
      leistungstemplate,
      leistung: leistungen.find((leistung) => leistung.leistungstemplateId === leistungstemplate.leistungstemplateId),
    }));
  });
  return (
    <Box
      display="grid"
      pl={8}
      pr={4}
      pt={2}
      pb={4}
      gap={2}
      position="relative"
      bgcolor={elevateColorPalette.surface.elevated}
    >
      <Typography variant="labelLg">Modultermine</Typography>
      <Box
        sx={{
          position: 'absolute',
          width: 16,
          height: 70,
          left: 24,
          top: 20,
          borderRadius: '0 0 0 4px',
          borderBottom: '1px solid',
          borderLeft: '1px solid',
          borderColor: elevateColorPalette.border.default,
        }}
      ></Box>
      <DataGridPro
        columns={modulterminColumns()}
        rows={rows}
        hideFooter
        getRowId={(row) => `${row.id}-${row.leistungstemplate?.leistungstemplateId ?? ''}`}
      />
    </Box>
  );
}

export const DurchgangListing = ({ thema }: DurchgangListingProps) => {
  const durchgangQuery = trpc.durchgang.getDurchgaengeByAkaProduktIds.useQuery({ akaProduktIds: [thema.akaProduktId] });
  const durchgaenge = durchgangQuery.data;

  const { enqueueSnackbar } = useSnackbar();

  const [filterModel, setFilterModel] = useState(
    localStorageApi.get(localStorageKeys.THEMA_VA_GRID_FILTERMODEL) ?? undefined,
  );
  const [sortModel, setSortModel] = useState<GridSortModel>(
    localStorageApi.get(localStorageKeys.THEMA_VA_GRID_SORTMODEL) ?? [{ sort: 'desc', field: 'start' }],
  );
  const [gridSelection, setGridSelection] = useState<GridRowSelectionModel>(() => ({
    type: 'include',
    ids: new Set(),
  }));

  const apiRef = useGridApiRef();
  const onRowClick = useCallback<GridEventListener<'rowClick'>>(
    (params) => {
      apiRef.current?.toggleDetailPanel(params.id);
    },
    [apiRef],
  );

  const changeStatusMutation = useDurchgaengeStatusMutation({ openLinksInNewTab: true });

  const handleChangeStatusForSelectedDurchgaenge = async (newStatus: VivaStatus): Promise<void> => {
    const selectionArray = Array.from(gridSelection.ids);
    if (selectionArray.length === 0) {
      enqueueSnackbar(content.keineDurchgaengeAuswahl, { variant: 'warning' });
      return;
    }

    const akaDurchgangIds = selectionArray
      .filter((id): id is string => typeof id === 'string')
      .filter((id): id is AkaDurchgangId => id.startsWith('D'));

    await changeStatusMutation.mutateAsync({
      newStatus,
      akaDurchgangIds,
    });
  };

  const statusTransitionsToShow = (durchgaenge ?? [])
    .filter((durchgang) => gridSelection.ids.has(durchgang.akaDurchgangId))
    .flatMap((durchgang) => durchgang.statusTransitions)
    .filter(unique)
    .toSorted(sortVivaStatus);

  return (
    <Grid container>
      <Grid size={{ xs: 12 }}>
        <Grid container justifyContent="end" sx={{ paddingBottom: 1 }}>
          {gridSelection.ids.size > 0 && (
            <Grid size={{ xs: 12 }} gap={2} display="flex">
              {statusTransitionsToShow.map((newStatus) => (
                <Button
                  key={newStatus}
                  disabled={changeStatusMutation.isPending}
                  variant="outlined"
                  onClick={() => void handleChangeStatusForSelectedDurchgaenge(newStatus)}
                >
                  {content.buttons[newStatus]}
                </Button>
              ))}
            </Grid>
          )}
        </Grid>
        <Stack spacing={1}>
          <TrpcLoadingInfo trpcQuery={durchgangQuery} entity="Durchgänge">
            <DataGridPro
              apiRef={apiRef}
              disableRowSelectionExcludeModel
              columns={durchgangsColumns()}
              rows={durchgaenge ?? []}
              checkboxSelection
              isRowSelectable={(params) => params.row.statusTransitions.length > 0}
              pagination
              pageSizeOptions={[5, 10, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
              filterModel={filterModel}
              onFilterModelChange={(newFilterModel) => {
                setFilterModel(newFilterModel);
                localStorageApi.set(localStorageKeys.THEMA_VA_GRID_FILTERMODEL, newFilterModel);
              }}
              sortModel={sortModel}
              onSortModelChange={(newSortModel) => {
                setSortModel(newSortModel);
                localStorageApi.set(localStorageKeys.THEMA_VA_GRID_SORTMODEL, newSortModel);
              }}
              disableRowSelectionOnClick
              onRowSelectionModelChange={(newSelection) => {
                setGridSelection(newSelection);
              }}
              rowSelectionModel={gridSelection}
              sx={{
                [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
                  outline: 'none',
                },
                [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: {
                  outline: 'none',
                },
                [`& .${gridClasses.detailPanelToggleCell}`]: {
                  paddingInline: 0,
                },
              }}
              getRowId={(durchgang) => durchgang.akaDurchgangId}
              getDetailPanelContent={(params) => getDetailPanelContent(thema, params)}
              getDetailPanelHeight={getDetailPanelHeight}
              slots={{
                detailPanelExpandIcon: DetailPanelExpandIcon,
                detailPanelCollapseIcon: DetailPanelCollapseIcon,
              }}
              onRowClick={onRowClick}
            />
          </TrpcLoadingInfo>
        </Stack>
      </Grid>
    </Grid>
  );
};
